/**
 * Reports Hub Page - PDF Export for Auditors
 * Generate, download, and manage compliance reports
 */
import React, { useState, useEffect } from 'react';
import {
  FileBarChart,
  Download,
  FileText,
  Calendar,
  Clock,
  CheckCircle2,
  Loader2,
  Filter,
  Eye,
  Shield,
  Database,
  BarChart3,
  FileSearch
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { apiFetch } from '../../services/api_service';


const REPORT_TYPES = [
  {
    id: 'compliance_summary',
    name: 'Compliance Summary',
    description: 'High-level overview of compliance status across all frameworks with evidence coverage metrics.',
    icon: Shield,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/20',
  },
  {
    id: 'evidence_register',
    name: 'Evidence Register',
    description: 'Complete register of all evidence documents with metadata, status, and framework mappings.',
    icon: Database,
    color: 'text-blue-400',
    bg: 'bg-blue-500/20',
  },
  {
    id: 'framework_specific',
    name: 'Framework Report',
    description: 'Detailed report for a specific regulatory framework with requirement coverage analysis.',
    icon: FileBarChart,
    color: 'text-purple-400',
    bg: 'bg-purple-500/20',
    requiresFramework: true,
  },
  {
    id: 'audit_trail',
    name: 'Audit Trail',
    description: 'Immutable record of all platform activities for external auditor verification.',
    icon: FileSearch,
    color: 'text-amber-400',
    bg: 'bg-amber-500/20',
  },
];

export default function ReportsHubPage() {
  const { toast } = useToast();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [selectedFramework, setSelectedFramework] = useState('CSRD');
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { 'Authorization': `Bearer ${getToken()}` };
        const [sumRes, histRes] = await Promise.all([
          apiFetch(`/v1/reports/summary`, { headers }),
          apiFetch(`/v1/reports/history`, { headers }),
        ]);
        if (sumRes && !sumRes._error) setSummary(sumRes);
        if (histRes && !histRes._error) {
          setHistory(histRes.reports || []);
        }
      } catch (e) {
        console.error('Failed to fetch report data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const generateReport = async (reportType) => {
    setGenerating(reportType);
    try {
      const params = new URLSearchParams({ report_type: reportType });
      if (reportType === 'framework_specific') {
        params.append('framework', selectedFramework);
      }

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/v1/reports/generate-pdf?${params}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      if (!res.ok) {
        throw new Error('Failed to generate report');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast({ title: 'Report Generated', description: 'Your PDF report has been downloaded.' });

      // Refresh history
      const histRes = await apiFetch(`/v1/reports/history`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (histRes && !histRes._error) {
        setHistory(histRes.reports || []);
      }
    } catch (err) {
      toast({ title: 'Generation Failed', description: err.message, variant: 'destructive' });
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-hub-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Reports & Export</h1>
        <p className="text-sm text-[#6B6B6B] mt-1">
          Generate audit-ready PDF reports for external auditors and compliance teams
        </p>
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Evidence Documents" value={summary.evidence_count} icon={Database} color="text-blue-400" />
          <StatCard label="Active Frameworks" value={summary.frameworks_count} icon={FileBarChart} color="text-purple-400" />
          <StatCard label="Reports Generated" value={summary.reports_generated} icon={Download} color="text-emerald-400" />
          <StatCard label="Available Exports" value={REPORT_TYPES.length} icon={FileText} color="text-amber-400" />
        </div>
      )}

      {/* Report Templates */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-3">Generate Report</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {REPORT_TYPES.map(type => (
            <div
              key={type.id}
              className="p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl hover:border-[#2F2F2F] transition-colors"
              data-testid={`report-type-${type.id}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`p-2 rounded-lg ${type.bg}`}>
                  <type.icon className={`w-5 h-5 ${type.color}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-white">{type.name}</h3>
                  <p className="text-xs text-[#6B6B6B] mt-1">{type.description}</p>
                </div>
              </div>

              {type.requiresFramework && (
                <div className="mb-3">
                  <label className="text-xs text-[#6B6B6B] mb-1 block">Select Framework</label>
                  <select
                    value={selectedFramework}
                    onChange={(e) => setSelectedFramework(e.target.value)}
                    className="w-full bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg px-3 py-2 text-sm text-white"
                    data-testid="framework-select"
                  >
                    {(summary?.available_frameworks || []).map(fw => (
                      <option key={fw.code} value={fw.code}>{fw.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <Button
                onClick={() => generateReport(type.id)}
                disabled={generating !== null}
                className="w-full bg-[#1F1F1F] hover:bg-[#2F2F2F] text-white border border-[#2F2F2F]"
                data-testid={`generate-${type.id}-btn`}
              >
                {generating === type.id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {generating === type.id ? 'Generating...' : 'Generate PDF'}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Report History */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
        <div className="p-4 border-b border-[#1F1F1F] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#6B6B6B]" />
            <h3 className="font-medium text-white">Report History</h3>
          </div>
          <Badge className="bg-[#1F1F1F] text-[#A0A0A0] border-0">{history.length} reports</Badge>
        </div>
        {history.length > 0 ? (
          <div className="divide-y divide-[#1F1F1F]">
            {history.map((report, i) => (
              <div key={i} className="p-3 flex items-center justify-between" data-testid={`history-item-${i}`}>
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-[#6B6B6B]" />
                  <div>
                    <p className="text-sm text-white">
                      {report.report_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </p>
                    <p className="text-xs text-[#6B6B6B]">
                      {report.framework || 'All Frameworks'} | {new Date(report.generated_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-[#6B6B6B]">
            <FileBarChart className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No reports generated yet</p>
            <p className="text-xs">Generate your first report above</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-[#6B6B6B]">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value ?? '-'}</p>
    </div>
  );
}
